import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { connect } from 'react-redux';
import {
  Components, Feedback, Utils, translate,
} from 'versafleet-core';
import * as Sentry from '@sentry/browser';
import JobModule from 'modules/job';
import ReduxActions from 'reduxActions';
import withRouterHooks from 'utils/routerHelper';
import { isUserReadOnly } from 'utils/accessHelper';

const {
  FullScreenLoading, Formatter, Helper, TranslationHelper, PusherHelper,
} = Utils;

const SORT_OPTIONS = [{
  value: 'id',
  text: 'Created at',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'updated_at',
  text: 'Last updated',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'jobs.time_from',
  text: 'Job Time window',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}];

class InProgressJobPageContent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      checkedStatus: {},
      filter: this._getDefaultFilter(),
      disableButton: { print: false },
      showFilter: false,
    };
  }

  UNSAFE_componentWillMount() {
    this.props.dispatch(ReduxActions.account.fetch());
    this.props.dispatch(ReduxActions.account.fetchTimeWindows());
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.filter !== this.state.filter) {
      this._loadList();
    }
    if (!isEqual(prevProps.meta, this.props.meta)) {
      this._updateCheckedStatus({});
    }
    if (!isEqual(prevProps.taskFilter, this.props.taskFilter)) {
      this._onModelChange('task');
    }
  }

  _toggleShowFilter = () => {
    this.setState(prevState => ({ showFilter: !prevState.showFilter }));
  }

  _resetFilters() {
    this.setState({ filter: this._getDefaultFilter() }, () => {
      this._loadList();
    });
  }

  _getDefaultFilter() {
    const { job_type: jobType, date } = this.props.filter;
    return {
      date: Array.isArray(date) ? date : [date, date],
      keyword: [],
      state: this._getJobStatusOptions().map(option => option.value),
      sort_by: SORT_OPTIONS[0].value,
      order_by: 'desc',
      page: 1,
      per_page: 20,
      time_window_id: [],
      time_from: null,
      time_to: null,
      tags: [],
      job_type: Helper.isNullOrUndefined(jobType)
        ? this._getJobTypeOptions().map(option => option.value) : jobType,
    };
  }

  _getStatusFilterText() {
    const { strings } = this.props;
    const { filter } = this.state;
    const taskStatusOptions = this._getJobStatusOptions();
    const taskStatusCount = filter.state.length > 0
    && filter.state.length <= taskStatusOptions.length ? filter.state.length : null;

    let text;
    if (taskStatusCount === 1) {
      text = Formatter.capitalizeFirstLetter(filter.state[0]);
    } else if (taskStatusCount === taskStatusOptions.length) {
      text = `${strings.Filter.buttons.all} ${strings.Filter.buttons.status.itemPlural}`;
    } else if (taskStatusCount === 0 || taskStatusCount === null) {
      text = `${strings.Filter.buttons.no} ${strings.Filter.buttons.status.item}`;
    } else {
      text = `${taskStatusCount} ${strings.Filter.buttons.status.itemPlural}...`;
    }

    return text;
  }

  _getTypeFilterText() {
    const { strings } = this.props;
    const { filter } = this.state;
    const jobTypeOptions = this._getJobTypeOptions();
    const taskTypeCount = filter.job_type.length > 0
    && filter.job_type.length <= jobTypeOptions.length ? filter.job_type.length : null;

    let text;
    if (taskTypeCount === 1) {
      text = strings.Filter.buttons.taskType.type[filter.job_type[0]];
    } else if (taskTypeCount === jobTypeOptions.length) {
      text = `${strings.Filter.buttons.all} ${strings.Filter.buttons.jobType.itemPlural}`;
    } else if (taskTypeCount === 0 || taskTypeCount === null) {
      text = `${strings.Filter.buttons.no} ${strings.Filter.buttons.jobType.item}`;
    } else {
      text = `${taskTypeCount} ${strings.Filter.buttons.jobType.itemPlural}...`;
    }

    return text;
  }

  _getTagFilterText() {
    const { jobTags, strings } = this.props;
    const value = this.state.filter.tags;
    const options = jobTags.map(option => ({ value: option, text: option }));

    const text = value.length <= 1 ? strings.Filter.buttons.tag.job.item : `${value.length > 1
      && value.length < options.length ? strings.Filter.buttons.tag.multiple
      : strings.Filter.buttons.all} ${strings.Filter.buttons.tag.job.itemPlural}`;

    return text;
  }

  _getSubTextJobTimeRange() {
    const { time_from: timeFrom, time_to: timeTo } = this.state.filter;

    return `${Helper.getTimeFromSecond(timeFrom * 60)} - ${Helper.getTimeFromSecond(timeTo * 60)}`;
  }

  _disableButton(buttonName) {
    this.setState(prevState => ({
      disableButton: { ...prevState.disableButton, [buttonName]: true },
    }), async () => {
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.setState(prevState => ({
        disableButton: { ...prevState.disableButton, [buttonName]: false },
      }));
    });
  }

  _getModelOptions() {
    const strings = this.props.strings.Job.constants;
    return [
      { value: 'task', text: Formatter.capitalizeFirstLetter(strings.taskPlural) },
      { value: 'job', text: Formatter.capitalizeFirstLetter(strings.jobPlural) },
    ];
  }

  _getJobStatusOptions() {
    const strings = this.props.strings.Job.status;
    return [{
      text: strings.in_progress,
      value: 'in_progress',
    }, {
      text: strings.completed,
      value: 'completed',
    }, {
      text: strings.cancelled,
      value: 'cancelled',
    }];
  }

  _getJobTypeOptions() {
    const strings = this.props.strings.Job.constants;
    return [{
      text: Formatter.capitalizeFirstLetter(strings.pickup),
      value: 'pickup',
    }, {
      text: Formatter.capitalizeFirstLetter(strings.delivery),
      value: 'delivery',
    }, {
      text: strings.pointToPoint,
      value: 'p2p',
    }];
  }

  _getSelectedIds() {
    return Object.keys(this.state.checkedStatus)
      .filter(key => this.state.checkedStatus[key])
      .map(key => parseInt(key, 10));
  }

  _onFilterChange = (fieldName, value) => {
    const { filter: filterState } = this.state;
    const filter = Helper.setFilter(filterState, fieldName, value);

    // Each time a filter is changed, check if any of the keys exist in
    // the filter store. If so, update the filter store to the filter changed
    const myJobsFilter = this.props.filter;
    Object.keys(myJobsFilter).forEach((key) => {
      if (typeof filter[key] !== 'undefined') {
        myJobsFilter[key] = filter[key];
      }
    });
    this.props.dispatch(ReduxActions.filter.updateMyJobsFilter(myJobsFilter));

    this.setState({ filter });
  }

  _onModelChange = (value) => {
    this.props.onModelChange(value);
  }

  _loadList() {
    FullScreenLoading.FullScreenLoadingManager.open(this.props.strings.Job.page.loadingItems);
    this.props.dispatch(ReduxActions.job.fetchList(this.state.filter))
      .then(() => {
        FullScreenLoading.FullScreenLoadingManager.close();
      });
  }

  _checkItem = (id) => {
    this.setState(prevState => ({
      checkedStatus: {
        ...prevState.checkedStatus,
        [id]: !prevState.checkedStatus[id],
      },
    }));
  }

  _updateCheckedStatus = (checkedStatus) => {
    this.setState({ checkedStatus });
  }

  _viewNewJobForm = () => {
    this.props.navigate('/in_progress/jobs/new');
  }

  _viewImportJobForm = () => {
    this.props.navigate('/in_progress/jobs/import');
  }

  _viewExportJobForm = () => {
    this.props.navigate('/in_progress/jobs/export');
  }

  _printMultiple = () => {
    const pusherId = `job_print_${PusherHelper.getNewPusherId()}`;
    const query = { ids: this._getSelectedIds(), pusher_id: pusherId };
    const printJobs = () => {
      PusherHelper.bindChannel('user', pusherId,
        result => this._handlePrintResult(result, pusherId));
      ReduxActions.job.printMultiple(query);
    };
    Feedback.Progress.Manager.updateProgress(pusherId, { onRetry: printJobs });
    printJobs();
    this._disableButton('print');
  }

  _handlePrintResult = (result, pusherId) => {
    Feedback.Progress.Manager.updateProgress(pusherId, result);
    if (result.status === 'failed') {
      PusherHelper.unbindChannel('user', pusherId);
      Sentry.captureException(result.error);
    }
    if (result.status === 'completed') {
      window.open(result.url, '_blank');
      PusherHelper.unbindChannel('user', pusherId);
    }
  }

  _renderFilterPanel = () => (
    <Components.ActionPanel.FilterPanel
      rightAnchoredButtons={[
        <Components.ActionPanel.FilterResetButton
          onClick={() => this._resetFilters()}
        />,
      ]}
      showFilter={this.state.showFilter}
    >
      <Components.ActionPanel.DropdownButton
        showArrow
        text={this._getStatusFilterText()}
      >
        <Components.Input.CheckboxGroup.WithArray
          onChange={value => this._onFilterChange('state', value)}
          options={this._getJobStatusOptions()}
          value={this.state.filter.state}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        subText={this.state.filter.tags.length === 1 ? this.state.filter.tags[0] : undefined}
        text={this._getTagFilterText()}
      >
        <Components.ActionPanel.FilterTagDropdown
          onChange={value => this._onFilterChange('tags', value)}
          options={this.props.jobTags.map(option => ({ value: option, text: option }))}
          placeholder={this.props.strings.Filter.buttons.tag.placeholder}
          value={this.state.filter.tags}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        subText={(!Helper.isNullOrUndefined(this.state.filter.time_from)
          && !Helper.isNullOrUndefined(this.state.filter.time_to))
          ? this._getSubTextJobTimeRange() : undefined}
        text={this.props.strings.Filter.buttons.timeRange.jobTimeRange}
      >
        <Components.ActionPanel.FilterTimeCheckbox
          onChange={(fieldName, v) => this._onFilterChange(fieldName, v)}
          options={this.props.timeWindows}
          values={this.state.filter}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        text={this._getTypeFilterText()}
      >
        <Components.Input.CheckboxGroup.WithArray
          onChange={value => this._onFilterChange('job_type', value)}
          options={this._getJobTypeOptions()}
          value={this.state.filter.job_type}
        />
      </Components.ActionPanel.DropdownButton>
    </Components.ActionPanel.FilterPanel>
  );

  _renderMultiSelectButtons() {
    return (
      <div>
        {this._renderNumSelectedItems(
          Formatter.capitalizeFirstLetter(this.props.strings.constants.job),
        )}
        {this._renderMultiPrintButton()}
      </div>
    );
  }

  _renderMultiSelectPanel = () => (
    <Components.ActionPanel.MultiSelectPanel
      checkedStatus={this.state.checkedStatus}
      dataIds={this.props.jobs.map(job => job.id)}
      updateCheckedStatus={this._updateCheckedStatus}
    >
      <span className="vf-job__page__multi-select-panel__count">
        {TranslationHelper.replaceTag(this.props.strings.Job.page.multiSelectCount, {
          count: (
            <span className="vf-job__page__multi-select-panel__count--number">
              {Object.keys(this.state.checkedStatus)
                .filter(key => this.state.checkedStatus[key]).length}
            </span>
          ),
        })}
      </span>

      <Components.Button
        disabled={this.state.disableButton.print}
        hint={this.props.strings.Job.page.buttons.print}
        iconClassName="versa-print"
        id="vf-in-progress__jobs__bulk-print-button"
        onClick={this._printMultiple}
      />
    </Components.ActionPanel.MultiSelectPanel>
  )

  _renderActionPanel() {
    const { meta, strings } = this.props;
    const { filter } = this.state;

    const sortOption = SORT_OPTIONS.find(option => option.value === filter.sort_by);
    const sortButtonText = Formatter.capitalizeEachWord(sortOption[`${filter.order_by}endingText`]);

    return (
      <Components.ActionPanel
        renderFilterPanel={this._renderFilterPanel}
        renderMultiSelectPanel={this._renderMultiSelectPanel}
      >
        <Components.Input.ButtonSelect
          id="vf-in-progress__jobs__model-toggle"
          onChange={this._onModelChange}
          options={this._getModelOptions()}
          value="job"
        />
        <Components.Paginator
          currentPage={meta.page}
          id="vf-in-progress__jobs__paginator"
          isCountCustomizable
          itemsPerPage={meta.per_page}
          model="jobs"
          onChange={this._onFilterChange}
          totalItems={meta.total}
        />
        <Components.ActionPanel.DropdownButton
          showArrow
          text={Helper.getRangeDateString(
            this.state.filter.date[0], this.state.filter.date[1], this.props.account.language,
          )}
        >
          <Components.ActionPanel.FilterDateRangePicker
            locale={this.props.account.language}
            onChange={value => this._onFilterChange('date', value)}
            values={this.state.filter.date}
          />
        </Components.ActionPanel.DropdownButton>
        <Components.ActionPanel.FilterTagSearch
          id="vf-in-progress__jobs__search-input"
          onChange={value => this._onFilterChange('keyword', value)}
        />
        <Components.ActionPanel.FilterShowButton
          onClick={() => { this._toggleShowFilter(); }}
          showFilter={this.state.showFilter}
        />
        <Components.ActionPanel.DropdownButton
          iconClassName="versa-sort"
          id="vf-in-progress__jobs__filter-tuner"
          text={sortButtonText}
        >
          <Components.Sorter
            id="vf-in-progress__jobs__filter-tuner__sort"
            onChange={value => this._onFilterChange(null, value)}
            options={SORT_OPTIONS}
            orderValue={this.state.filter.order_by}
            value={this.state.filter.sort_by}
          />
        </Components.ActionPanel.DropdownButton>
        <Components.ActionPanel.ButtonGroup>
          {!isUserReadOnly() ? (
            <Components.Button
              hint={strings.Job.page.buttons.add}
              iconClassName="versa-add"
              id="vf-in-progress__jobs__new-job-button"
              onClick={this._viewNewJobForm}
            />
          ) : false}
          {!isUserReadOnly() ? (
            <Components.Button
              hint={strings.Job.page.buttons.import}
              iconClassName="versa-upload"
              id="vf-in-progress__jobs__import-button"
              onClick={this._viewImportJobForm}
            />
          ) : false}
          <Components.Button
            hint={strings.Job.page.buttons.export}
            iconClassName="versa-download"
            id="vf-in-progress__jobs__export-button"
            onClick={this._viewExportJobForm}
          />
        </Components.ActionPanel.ButtonGroup>
      </Components.ActionPanel>
    );
  }

  _renderPageContent() {
    return this.props.jobs.map(job => (
      <JobModule.ListItem
        account={this.props.account}
        checkItem={this._checkItem}
        checkedStatus={this.state.checkedStatus[job.id]}
        data={job}
        dispatch={this.props.dispatch}
        id="vf-in-progress__jobs__list-item"
        key={job.id}
      />
    ));
  }

  render() {
    return (
      <div>
        {this._renderActionPanel()}
        {this._renderPageContent()}
        {this.props.children}
      </div>
    );
  }
}

InProgressJobPageContent.displayName = 'In Progress Job Page Content';

InProgressJobPageContent.propTypes = {
  account: PropTypes.shape().isRequired,
  children: PropTypes.node,
  dispatch: PropTypes.func.isRequired,
  filter: PropTypes.shape({
    date: PropTypes.string,
    job_type: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  jobTags: PropTypes.arrayOf(PropTypes.string),
  jobs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  meta: PropTypes.shape({
    page: PropTypes.number,
    per_page: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  navigate: PropTypes.func.isRequired,
  onModelChange: PropTypes.func.isRequired,
  strings: PropTypes.shape().isRequired,
  taskFilter: PropTypes.shape(),
  timeWindows: PropTypes.arrayOf(PropTypes.shape()),
};

InProgressJobPageContent.defaultProps = {
  children: null,
  jobTags: [],
  taskFilter: {},
  timeWindows: [],
};

function mapStateToProps(state) {
  const {
    account, job, filter, constant,
  } = state;

  return {
    account: account.account,
    meta: job.jobMeta,
    jobs: job.jobsIds.map(id => job.jobs[id]),
    isLoading: job.isLoading,
    isLoadingItem: job.isLoadingItem,
    filter: filter.filter.myJobs,
    jobTags: constant.constant.jobs.tags,
    taskFilter: filter.filter.task,
    timeWindows: account.timeWindows,
  };
}

export default withRouterHooks(connect(mapStateToProps)(translate('')(InProgressJobPageContent)));
